import * as types from './actionTypes';
import moment from 'moment';
import {
    IWorkOrder,
    IinstallBasePopulated,
    ImeasurementPointAnswer,
    ImeasurementPointResult,
    ThunkResult,
    ImeasurementPointResultWithType,
    IdamageCode,
    IopenRepairConflict
} from '../models';
import filter from 'lodash/filter';
import map from 'lodash/map';
import find from 'lodash/find';
import { jobTypesIdEnum, measurementPointResultStatusTypesEnum } from '../models-enums';

import API from '../constants/apiEndpoints';
import { TFunction } from 'i18next';
import { completeWorkOrdersHelper } from './workOrderActions';
import { commitMaintenanceTriggersForInstallBase } from './maintenanceTriggerActions';
import { getPendingMaintenanceTriggersByInstallBase } from '../reducers/maintenanceTriggerReducer';
import { constants } from '../constants/constants';
import { getIsWorkOrderTypeJob } from '../reducers/manageJobReducer';
import { selectMeasurementPointListForInstallBase } from '../reducers/measurementPointListsReducer';
import { selectWorkOrdersByInstallBaseID } from '../reducers/workOrderReducer';
import { toastr } from 'react-redux-toastr';
import { getMeasurementPointListResultsForJobsHelper } from './workerActions';
import {
    initInstallBaseResult,
    mergeMaintenanceAnswers,
    prepareMeasurementPointListResults,
    validateNewResult
} from './measurementPointResultUtil';
// import { latestResults } from '../reducers/manageInventoryReducer';
import { selectInstallBaseHistoryByInstallBaseID } from '../reducers/commonReducers';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { filterInstallBases } from '../reducers/manageInventoryReducer';
import { msalFetch } from '../components/auth/Auth-Utils';
import { v4 as uuidv4 } from 'uuid';

export function updateMeasurementPointResult(
    result: ImeasurementPointResult,
    historicalResult: boolean
): ThunkResult<any> {
    return dispatch => {
        dispatch({
            type: types.UPDATE_MEASUREMENT_POINT_RESULT,
            result
        });

        if (historicalResult) {
            dispatch({
                type: types.SET_HISTORICAL_RESULT_ID,
                resultID: result.id
            });
        }
    };
}

export function getPagedMPLRForJob(
    jobID: string,
    facilityID: string,
    page: number,
    pageSize: number,
    lastPage: boolean
): ThunkResult<any> {
    return (dispatch, getState) => {
        let startTime: number;
        let lastLoaded = 0;
        let packetCount = 0;

        const root = process.env.REACT_APP_SERVER_DOMAIN;
        const url = `${root}/MeasurementPoint/GetPagedMeasurementPointListResultsForJob?JobID=${jobID}&page=${page}&perPage=${pageSize}&lastPage=${lastPage}`;

        let axiosOptions: AxiosRequestConfig = {
            method: 'get',
            timeout: 1000 * 60 * 5,
            onDownloadProgress: progressEvent => {
                const currentTime = new Date().getTime();
                if (!startTime) {
                    startTime = currentTime;
                }
                const timeElapsedInSeconds = (currentTime - startTime) / 1000;
                const loadedBits = progressEvent.loaded * 8 - lastLoaded;
                const speedInBps = loadedBits / timeElapsedInSeconds;
                const speedInMbps = speedInBps / 1_000_000;

                if (packetCount % 10 === 0) {
                    dispatch({ type: types.SET_DOWNLOAD_SPEED, downloadSpeed: speedInMbps.toFixed(2) });
                }

                packetCount++;
                lastLoaded = progressEvent.loaded;
            }
        };

        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                if (!data.data) {
                    throw Error('Failed to get MPLR for Job');
                } else {
                    dispatch({
                        type: types.GET_MEASUREMENT_POINT_FACILITY_RESULTS_SUCCESS,
                        results: data.data,
                        facilityID,
                        updateDate: moment().unix()
                    });
                }
            })
            .catch((error: any) => {
                console.error('getPagedMPLRForJob', error);
            });
    };
}

export function saveDamageCode(result: IdamageCode, installBaseID: string): ThunkResult<any> {
    return dispatch => {
        dispatch({
            type: types.SAVE_DAMAGE_CODES_SUCCESS,
            result,
            installBaseID
        });
    };
}

export function getMeasurementPointListResultsForJobs(jobId: string, facilityID: string): ThunkResult<any> {
    return dispatch => {
        dispatch(getMeasurementPointListResultsForJobsHelper(jobId, facilityID));
    };
}

/*
 * If we are in Historical mode, load the historical Result.  Otherwise:
 * 1) load the result that has been edited (temporary)
 * 2) load a previous result we use to copy to a new result
 * 3) create a completely new result
 */
export const initSelectedResult = (installID: string): ThunkResult<any> => {
    return (dispatch, getState) => {
        const { selectedJob } = getState().manageJob;
        const { historicalResultID, measurementPointResultsByID } = getState().measurementPointResults;
        const filteredMPR = Object.values(measurementPointResultsByID).filter(
            result => result.jobID === selectedJob.id
        );

        if (historicalResultID.length) {
            const historicalResult = filteredMPR.find(result => result.id === historicalResultID);

            if (historicalResult) {
                return dispatch(updateMeasurementPointResult(historicalResult, true));
            }
        } else {
            dispatch(updateMeasurementPointResult(initInstallBaseResult(getState, { installID }), false));
        }
    };
};

/*
 * Loading state comes from redux-offline's own busy flag (see selectIsLoading), which covers the
 * effect from queue to commit. Do NOT add beginAjaxCall() here: ajaxCallsInProgress is only ever
 * decremented by an action type ending in _SUCCESS/_FAILED or by an explicit endAjaxCall, and this
 * action commits SET_MEASUREMENT_POINT_RESULT_HISTORY - so each Load History click leaked a count
 * and pinned the top bar spinner until some unrelated call happened to finish.
 */
export const getMeasurementPointListResultHistory = (installBaseId: string): ThunkResult<any> => {
    return dispatch => {
        // No job type filter (MMG-4345): the endpoint returns every closed job's results for this
        // asset and MeasurementPointListHistory decides what to show.
        const url = `${API.GET.measurementPoint.getMeasurementPointListResultHistory}?installBaseId=${installBaseId}`;
        const axiosOptions: AxiosRequestConfig = {
            url,
            method: 'get'
        };

        dispatch({
            type: types.GET_MEASUREMENT_POINT_RESULT_HISTORY,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'Get MPLR History' },
                    commit: {
                        type: types.SET_MEASUREMENT_POINT_RESULT_HISTORY
                    }
                }
            }
        });
    };
};

const submitMeasurementPointResultHelper = (
    updatedResults: ImeasurementPointResult[],
    isBulk = false,
    overrideOpenRepair = false,
    jobTypeIDOverride?: string
): ThunkResult<any> => {
    return (dispatch, getState) => {
        // we can send slimmed down work order objects;
        let results = prepareMeasurementPointListResults(updatedResults);
        // a retry can run long after the user left the job, so the caller pins the job type
        const jobTypeID = jobTypeIDOverride || getState().manageJob.selectedJob.jobTypeID;

        // the server persists savedWithOpenRepair and force-fails the asset at job close
        if (overrideOpenRepair) {
            results = map(results, result => ({ ...result, savedWithOpenRepair: true }));
        }

        // Damage codes
        for (let i = 0; i < results.length; i++) {
            const result = results[i];
            if (!result) {
                console.error('Result is undefined at index:', i);
                continue;
            }

            // Look for any damage codes for this install base id
            const installBaseID = result.installBaseID;
            const damageCodeIndex = Object.keys(getState().manageInventory.damageCodes).indexOf(installBaseID);
            let damageCode: any = null;
            if (damageCodeIndex > -1) {
                damageCode = Object.values(getState().manageInventory.damageCodes)[damageCodeIndex];
            }

            result.DamageCode = damageCode;
        }

        const batchAmount = 25;

        for (let i = 0; i < results.length; i += batchAmount) {
            const batch = results.slice(i, i + batchAmount);
            const lastRun = batch.length < batchAmount;

            const measurementPointResultsForRollback = map(batch, measurementPointResult => ({
                ...measurementPointResult,
                isDeleted: true
            }));

            // TODO clean the results we send to the API
            const axiosOptions = {
                url: overrideOpenRepair
                    ? `${API.POST.measurementPoint.addResults}?overrideOpenRepair=true`
                    : API.POST.measurementPoint.addResults,
                method: 'post',
                data: batch,
                timeout: 600_000
            };

            dispatch({
                type: types.UPDATE_MEASUREMENT_POINT_RESULT_BULK,
                results: batch,
                jobTypeID,
                meta: {
                    offline: {
                        effect: { axiosOptions, message: 'Saving Answers' },
                        rollback: {
                            type: types.UPDATE_MEASUREMENT_POINT_RESULT_BULK,
                            results: measurementPointResultsForRollback,
                            jobTypeID
                        },
                        // if it's the last run of the batch, need to update the inventory, creating a random guid to make sure the useEffect fires each time
                        commit:
                            lastRun && isBulk
                                ? { type: types.UPDATE_MEASUREMENT_POINT_RESULT_BULK_SUCCESS, meta: { id: uuidv4() } }
                                : {}
                    }
                }
            });
        }

        // Clear the selection of the table
        dispatch({
            type: types.INSTALL_UPDATE_SELECTION,
            payload: []
        });
    };
};

export function submitMeasurementPointResult(
    updatedResult: ImeasurementPointResult,
    updatedAnswers: ImeasurementPointAnswer[],
    selectedWorkOrders: IWorkOrder[],
    overrideOpenRepair = false,
    complianceConfirmed?: boolean
): ThunkResult<any> {
    return (dispatch, getState) => {
        const combinedAnswers = map(updatedAnswers, answer => {
            const originalAnswer = find(updatedResult.measurementPointAnswers, {
                measurementPointID: answer.measurementPointID
            });
            return { ...originalAnswer, ...answer };
        });

        const pendingTriggers = getPendingMaintenanceTriggersByInstallBase(
            getState(),
            updatedResult.installBaseID
        ).map(t => {
            const withId = t.id ? t : { ...t, id: uuidv4() };
            // A cleared date is staged as '' for the editor; strip it before sending - an empty string
            // is not a valid DateTime? and fails server deserialization (-> 400). (The answer's dateValue
            // is already omitted when empty, and the backend ignores the trigger's ManualEntryDate.)
            if (withId.manualEntryDate) return withId;
            const cleared = { ...withId };
            delete cleared.manualEntryDate;
            return cleared;
        });

        // For each pending Maintenance trigger that knows its MP, derive a MeasurementPointAnswer carrying
        // every editable field: isEnabled (TE), isSnoozed (SNO), isUnknown (UNK), pass (WMC: 1=Yes/0=No)
        // and dateValue (MED). The answer is the source of truth - CompleteJob projects these onto the
        // trigger at close.
        const maintenanceAnswers: ImeasurementPointAnswer[] = pendingTriggers
            .filter(t => !!t.measurementPointID)
            .map(t => ({
                measurementPointID: t.measurementPointID as string,
                showInReport: false,
                isEnabled: t.isEnabled,
                isSnoozed: t.isSnoozed,
                isUnknown: !!t.isUnknown,
                pass: t.maintenanceCompleted ? 1 : 0,
                ...(t.manualEntryDate ? { dateValue: t.manualEntryDate } : {})
            }));

        // Maintenance MPs don't flow through the form, so they aren't re-emitted on each save. Carry
        // untouched maintenance answers forward so each saved snapshot stays complete (see helper).
        const combinedAnswersWithMaintenance = mergeMaintenanceAnswers(
            combinedAnswers,
            updatedResult.measurementPointAnswers,
            maintenanceAnswers,
            getState().measurementPointLists.measurementPointsByID
        );

        const updatedResultWithAnswers: ImeasurementPointResult = {
            ...updatedResult,
            measurementPointAnswers: combinedAnswersWithMaintenance,
            ...(pendingTriggers.length ? { maintenanceTriggers: pendingTriggers } : {})
        };
        // double check that the installBase exists
        const { manageInventory } = getState();

        const installBase = manageInventory.installBasesByID[updatedResultWithAnswers.installBaseID];

        if (!installBase) {
            console.error('[submitMeasurementPointResult]:', updatedResultWithAnswers);
            throw new Error('Error, missing asset. Please contact support.');
        }

        if (selectedWorkOrders.length) {
            // MMG-4335: the repair compliance answer rides on the work order; the server applies it in
            // CompleteWorkOrder and it then feeds the asset's ComplianceStatus at job close
            const workOrdersToComplete =
                complianceConfirmed === undefined
                    ? selectedWorkOrders
                    : selectedWorkOrders.map(workOrder => ({ ...workOrder, complianceConfirmed }));

            dispatch(
                completeWorkOrdersHelper(workOrdersToComplete, 'activeClosingNotes', 'closingNotes', 'closingNotesList')
            );
        }

        dispatch(submitMeasurementPointResultHelper([updatedResultWithAnswers], false, overrideOpenRepair));

        if (pendingTriggers.length) {
            dispatch(commitMaintenanceTriggersForInstallBase(updatedResultWithAnswers.installBaseID));
        }
    };
}

/**
 * Open repair work orders blocking an inspection Save & Log for this asset (MMG-4336).
 * Returns an empty list when offline or on any failure - the API's 409 at sync time is the
 * authority, this is only the online pre-check so the user is warned before queueing.
 */
export function checkOpenRepairConflicts(installBaseID: string): ThunkResult<Promise<IopenRepairConflict[]>> {
    return async (dispatch, getState) => {
        if (!getState().offline.online) {
            return [];
        }

        try {
            const url = `${API.inventory.openConflicts}?installBaseID=${installBaseID}`;
            const response = await msalFetch(url, { url, method: 'get' });
            return response.data?.openRepairWorkOrders || [];
        } catch (error) {
            console.error('[checkOpenRepairConflicts]:', error);
            return [];
        }
    };
}

export function clearOpenRepairConflicts(): ThunkResult<any> {
    return dispatch => {
        dispatch({ type: types.CLEAR_OPEN_REPAIR_CONFLICTS });
    };
}

/**
 * Re-send the answers the API rejected for an open repair, this time with the override so they
 * persist. The asset is force-failed at job close by the server.
 */
export function submitBlockedResultsWithOverride(): ThunkResult<any> {
    return (dispatch, getState) => {
        const { blockedResults } = getState().openRepairConflicts;
        dispatch({ type: types.CLEAR_OPEN_REPAIR_CONFLICTS });

        if (!blockedResults.length) {
            return;
        }

        dispatch(submitMeasurementPointResultHelper(blockedResults, false, true, blockedResults[0]?.jobTypeID));
    };
}

/*
 * updatedAnswers is the whole form, so the stored answers now hold what the engineer sees. Stamping
 * answersSeededFrom records that, so a later control rebuild does not re-fill the draft from history.
 */
export function updateMprAnswers(
    updatedAnswers: ImeasurementPointAnswer[],
    answersSeededFrom: string | undefined
): ThunkResult<any> {
    return (dispatch, getState) => {
        const { selectedResult } = getState().measurementPointResults;
        const combinedAnswers = map(updatedAnswers, answer => {
            const originalAnswer = find(selectedResult.measurementPointAnswers, {
                measurementPointID: answer.measurementPointID
            });
            return { ...originalAnswer, ...answer };
        });
        dispatch(
            updateMeasurementPointResult(
                { ...selectedResult, measurementPointAnswers: combinedAnswers, answersSeededFrom },
                false
            )
        );
    };
}

export function markGatePicked(gateID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const { selectedResult } = getState().measurementPointResults;
        const pickedGateIDs = selectedResult.pickedGateIDs ?? [];
        if (pickedGateIDs.includes(gateID)) {
            return;
        }
        dispatch(updateMeasurementPointResult({ ...selectedResult, pickedGateIDs: [...pickedGateIDs, gateID] }, false));
    };
}

export function bulkCannotCompleteSelected(
    notes: string,
    installBasesPopulated: IinstallBasePopulated[]
): ThunkResult<any> {
    return (dispatch, getState) => {
        const { manageInventory } = getState();
        const { selection } = manageInventory;
        const { selectedJob } = getState().manageJob;
        const facility = getState().facilities.facilitiesByID[selectedJob.facilityID];

        if (!facility) {
            console.error('Facility not found:', selectedJob.facilityID);
            return;
        }

        const selectedInstallBaseIDs = selection.map(item => {
            return item.split('select-')[1];
        });

        // TODO turn into a helper function
        const uninspectedInstalls = filter(installBasesPopulated, install => {
            let shouldInclude = true;
            if (selectedInstallBaseIDs.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (
                install.latestMeasurementPointListResult.status !==
                measurementPointResultStatusTypesEnum.resultStatusNotTested
            ) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        let cannotCompleteResultsCollection: ImeasurementPointResult[] = [];
        let errorCollection: string[] = [];
        uninspectedInstalls.forEach(install => {
            const selectedMeasurementPointList = selectMeasurementPointListForInstallBase(getState(), {
                materialID: install.materialID,
                standardID: facility.standardID,
                shouldShowWarning: false
            });

            if (selectedMeasurementPointList.id.length === 0) {
                errorCollection = [...errorCollection, 'missing mpl'];
                return;
            }
            const newResult = initInstallBaseResult(getState, {
                installID: install.id
            });

            const cannotCompleteResult = {
                ...newResult,
                status: measurementPointResultStatusTypesEnum.resultStatusCannotComplete,
                notes
            };
            cannotCompleteResultsCollection = [...cannotCompleteResultsCollection, cannotCompleteResult];
        });

        if (errorCollection.length) {
            toastr.warning(
                'Warning',
                'Some assets do not have measurement point lists and were not submitted.',
                constants.toastrWarning
            );
        } else {
            toastr.success('Success', 'All devices updated successfully.', constants.toastrSuccess);
        }

        // submit the successul results
        dispatch(submitMeasurementPointResultHelper(cannotCompleteResultsCollection, true));
    };
}

export function startBulkCannotCompleteSelected(
    installBasesPopulated: IinstallBasePopulated[],
    t: TFunction
): ThunkResult<any> {
    return (dispatch, getState) => {
        const { manageInventory } = getState();
        const { selection } = manageInventory;

        const selectedInstallBaseIDs = selection.map(item => {
            return item.split('select-')[1];
        });

        const uninspectedInstalls = filter(installBasesPopulated, install => {
            let shouldInclude = true;
            if (selectedInstallBaseIDs.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (
                install.latestMeasurementPointListResult.status !==
                measurementPointResultStatusTypesEnum.resultStatusNotTested
            ) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        if (!uninspectedInstalls.length) {
            toastr.warning('Warning', 'All devices have already been inspected.', constants.toastrWarning);
            return;
        }

        const toastrConfirmOptions = {
            onOk: () => {
                dispatch({ type: types.TOGGLE_MODAL_NOTE_PAD });
            },
            onCancel: () => null,
            okText: t('bulkCannotCompleteConfirmButton'),
            cancelText: t('common:cancel')
        };
        toastr.confirm(
            t('confirmBulkCannotCompleteInstallsTitle', {
                count: selection.length
            }),
            toastrConfirmOptions
        );
    };
}

const isObjectAMeasurementPointResult = (object: any): object is ImeasurementPointResultWithType => {
    if (object === undefined) {
        return false;
    }

    return 'jobID' in object;
};

/*
 * passSelectedHelper
 * skip saving a result for this install if there is already a result for this install and job,
 * the most recent result for this install from another job is a fail
 */
const passSelectedHelper = (
    uninspectedInstalls: IinstallBasePopulated[],
    t: TFunction,
    closingNotes?: string
): ThunkResult<any> => {
    return (dispatch, getState) => {
        try {
            const { manageJob, facilities } = getState();
            const { selectedJob } = manageJob;
            const isWorkOrderTypeJob = getIsWorkOrderTypeJob(getState());
            const facility = facilities.facilitiesByID[selectedJob.facilityID];

            if (!facility) {
                console.error('Facility not found:', selectedJob.facilityID);
                return;
            }

            let toastSuccess = 'toastMessage:mobileBulkPassResultsSuccessMessage';
            let toastFail = 'toastMessage:mobileBulkPassResultsNotAllPassedWarningMessage';

            if (isWorkOrderTypeJob) {
                toastSuccess = 'toastMessage:mobileBulkPassWOsSuccessMessageWO';
                toastFail = 'toastMessage:mobileBulkPassWOsNotAllPassedWarningMessageWO';
            }
            let resultErrorCollection: Array<{
                installID: string;
                resultErrorMessages: string[];
            }> = [];
            let passingResultsCollection: ImeasurementPointResult[] = [];
            let workOrdersCollection: IWorkOrder[] = [];
            uninspectedInstalls.forEach(install => {
                const selectedMeasurementPointList = selectMeasurementPointListForInstallBase(getState(), {
                    materialID: install.materialID,
                    standardID: facility.standardID,
                    shouldShowWarning: false
                });

                if (selectedMeasurementPointList.id.length) {
                    const newResult = initInstallBaseResult(getState, {
                        installID: install.id
                    });

                    // If the measurementPointResult has a status of Not Tested, let's double check the history and make sure, we CANNOT Pass an asset if it's last status was Failed
                    if (newResult.status === measurementPointResultStatusTypesEnum.resultStatusNotTested) {
                        const installBaseHistory = selectInstallBaseHistoryByInstallBaseID(getState(), install.id);
                        let lastMP: IWorkOrder | ImeasurementPointResultWithType | undefined;

                        for (const value of installBaseHistory) {
                            // Check if the value is an IWorkOrder or ImeasurementPointResultWithType
                            if (isObjectAMeasurementPointResult(value)) {
                                const tempMPR = value as ImeasurementPointResultWithType;

                                // Make sure the item in History, is of the same job type we are currently on
                                if (tempMPR.jobTypeID !== undefined && selectedJob.jobTypeID === tempMPR.jobTypeID) {
                                    lastMP = value as ImeasurementPointResultWithType;
                                    break;
                                }
                            } else {
                                // this is an IWorkOrder, don't think we care about this one...
                                lastMP = undefined;
                            }
                        }

                        // So we found a MPR in history for this job type, that has a different status, we'll use that one
                        if (
                            isObjectAMeasurementPointResult(lastMP) &&
                            lastMP.measurementPointAnswers !== undefined &&
                            newResult.status !== lastMP.status
                        ) {
                            newResult.status = lastMP.status;
                        }
                    }

                    // validate the new result
                    const resultErrorMessages = validateNewResult(selectedMeasurementPointList, newResult, getState);
                    if (!resultErrorMessages.length) {
                        let newStatus = measurementPointResultStatusTypesEnum.resultStatusPass;
                        if (selectedJob.jobTypeID === jobTypesIdEnum.repair) {
                            newStatus = measurementPointResultStatusTypesEnum.resultStatusRepaired;
                        }
                        if (
                            selectedJob.jobTypeID === jobTypesIdEnum.maintenance ||
                            selectedJob.jobTypeID === jobTypesIdEnum.servicePlan
                        ) {
                            newStatus = measurementPointResultStatusTypesEnum.resultStatusMaintain;
                        }
                        if (selectedJob.jobTypeID === jobTypesIdEnum.commissioning) {
                            newStatus = measurementPointResultStatusTypesEnum.resultStatusCommissioned;
                        }
                        const passingResult = {
                            ...newResult,
                            status: newStatus
                        };
                        passingResultsCollection = [...passingResultsCollection, passingResult];
                        if (isWorkOrderTypeJob && closingNotes) {
                            const selectedWorkOrders = selectWorkOrdersByInstallBaseID(getState(), install.id);
                            const selectedWOsWithNotes = map(selectedWorkOrders, wo => {
                                return { ...wo, closingNotes };
                            });
                            workOrdersCollection = [...workOrdersCollection, ...selectedWOsWithNotes];
                        }
                    } else {
                        resultErrorCollection = [
                            ...resultErrorCollection,
                            { installID: install.id, resultErrorMessages }
                        ];
                    }
                } else {
                    resultErrorCollection = [
                        ...resultErrorCollection,
                        {
                            installID: install.id,
                            resultErrorMessages: ['missing measurement point list']
                        }
                    ];

                    console.error('[passSelectedHelper]: missing measurement point list');
                }
            });

            // Mainly for passingResultsCollection, we want to wait for all API calls to finish before showing a success prompt
            // Success prompt will actually happen in manageInventory when shouldRefreshInventory useEffect fires
            let showSucessPrompt = true;

            // submit the results that passed validation
            if (passingResultsCollection.length) {
                showSucessPrompt = false;
                dispatch(submitMeasurementPointResultHelper(passingResultsCollection, true));
            }

            if (isWorkOrderTypeJob && workOrdersCollection.length) {
                dispatch(completeWorkOrdersHelper(workOrdersCollection));
            }

            if (resultErrorCollection.length) {
                toastr.warning(t('toastMessage:warning'), t(toastFail), constants.toastrWarning);

                resultErrorCollection.forEach(resultError => {
                    const errorBase = uninspectedInstalls.find(install => install.id === resultError.installID);

                    toastr.warning(
                        t('toastMessage:error'),
                        `${errorBase?.productNameString}.\nThe asset failed during last inspection. Batch function "Pass" would not be applied to it.`,
                        constants.toastrError
                    );
                });
            } else {
                if (showSucessPrompt) {
                    toastr.success(t('success'), t(toastSuccess), constants.toastrSuccess);
                }
            }
        } catch (error) {
            console.error('[passSelectedHelper]:', error);
        } finally {
            dispatch({ type: types.UPDATE_MANAGE_INVENTORY_LOADING, payload: false });
        }
    };
};

/*
 * Loop over all the selected devices
 * find all the uninspected installs and send those to the passSelectedHelper
 * If no uninspected, then show a warning
 */
export function passSelected(t: TFunction, closingNotes?: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const { manageInventory } = getState();
        const installBases = filterInstallBases(getState());
        const { selection } = manageInventory;
        const selectedInstallBaseIDs = selection.map(item => {
            return item.split('select-')[1];
        });

        // we only want to grab installs that have not been tested for this job yet
        const uninspectedInstalls = filter(installBases, install => {
            let shouldInclude = true;
            if (selectedInstallBaseIDs.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (
                install.latestMeasurementPointListResult.status !==
                measurementPointResultStatusTypesEnum.resultStatusNotTested
            ) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        if (!uninspectedInstalls.length) {
            toastr.warning('Warning', 'All devices have already been inspected.', constants.toastrWarning);
            return;
        }

        const toastrConfirmOptions = {
            onOk: () => {
                dispatch({ type: types.UPDATE_MANAGE_INVENTORY_LOADING, payload: true });
                setTimeout(() => {
                    dispatch(passSelectedHelper(uninspectedInstalls, t, closingNotes));
                }, 100);
            },
            onCancel: () => null,
            okText: t('buttonBulkPass'),
            cancelText: t('common:cancel')
        };
        toastr.confirm(
            t('confirmBulkPassInstalls', {
                count: selection.length
            }),
            toastrConfirmOptions
        );
    };
}

const updateSelectedStatusHelper = (
    installsToUpdate: IinstallBasePopulated[],
    status: measurementPointResultStatusTypesEnum,
    t: TFunction
): ThunkResult<any> => {
    return (dispatch, getState) => {
        try {
            const { manageJob, facilities } = getState();
            const { selectedJob } = manageJob;
            const facility = facilities.facilitiesByID[selectedJob.facilityID];

            let resultErrorCollection: Array<{
                installID: string;
                resultErrorMessages: string[];
            }> = [];

            let updatedResultsCollection: ImeasurementPointResult[] = [];

            installsToUpdate.forEach(install => {
                if (!facility) {
                    console.error(`[updateSelectedStatusHelper]: facility not found for install ${install.id}`);

                    return;
                }

                const selectedMeasurementPointList = selectMeasurementPointListForInstallBase(getState(), {
                    materialID: install.materialID,
                    standardID: facility.standardID,
                    shouldShowWarning: false
                });

                if (selectedMeasurementPointList.id.length) {
                    const newResult = initInstallBaseResult(getState, {
                        installID: install.id
                    });

                    const updatedResult = {
                        ...newResult,
                        status
                    };
                    updatedResultsCollection = [...updatedResultsCollection, updatedResult];

                    resultErrorCollection = [
                        ...resultErrorCollection,
                        { installID: install.id, resultErrorMessages: [] }
                    ];
                } else {
                    resultErrorCollection = [
                        ...resultErrorCollection,
                        {
                            installID: install.id,
                            resultErrorMessages: ['missing measurement point list']
                        }
                    ];

                    console.error(`[updateSelectedStatusHelper]: missing measurement point list`);
                }
            });

            if (updatedResultsCollection.length) {
                dispatch(submitMeasurementPointResultHelper(updatedResultsCollection, true));
            }

            const isCurrentJobDecommissioningOrNotUnderContract =
                status === measurementPointResultStatusTypesEnum.resultStatusDecommissioned ||
                status === measurementPointResultStatusTypesEnum.resultStatusNotUnderContract;

            if (isCurrentJobDecommissioningOrNotUnderContract) {
                console.debug('Bypassing missing measurement point list error for status', status);
                return;
            }
        } catch (error) {
            console.error('[updateSelectedStatusHelper]:', error);
        } finally {
            dispatch({ type: types.UPDATE_MANAGE_INVENTORY_LOADING, payload: false });
        }
    };
};

export function markSelectedAs(
    status:
        | measurementPointResultStatusTypesEnum.resultStatusDecommissioned
        | measurementPointResultStatusTypesEnum.resultStatusNotUnderContract,
    t: TFunction
): ThunkResult<any> {
    return (dispatch, getState) => {
        const { manageInventory } = getState();
        const installBases = filterInstallBases(getState());
        const { selection } = manageInventory;
        const selectedIds = selection.map(item => {
            return item.split('select-')[1];
        });

        const installsToUpdate = filter(installBases, install => {
            let shouldInclude = true;
            if (selectedIds.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        if (!installsToUpdate.length) {
            toastr.warning('Warning', 'All devices have already been inspected.', constants.toastrWarning);
            return;
        }

        const okText =
            status === measurementPointResultStatusTypesEnum.resultStatusDecommissioned
                ? t('buttonBulkDecommission')
                : t('buttonBulkNotUnderContract');
        const confirmText =
            status === measurementPointResultStatusTypesEnum.resultStatusDecommissioned
                ? t('confirmBulkDecommissionInstalls', { count: selection.length })
                : t('confirmBulkNotUnderContractInstalls', { count: selection.length });

        const toastrConfirmOptions = {
            onOk: () => {
                dispatch({ type: types.UPDATE_MANAGE_INVENTORY_LOADING, payload: true });
                setTimeout(() => {
                    dispatch(updateSelectedStatusHelper(installsToUpdate, status, t));
                }, 100);
            },
            onCancel: () => null,
            okText,
            cancelText: t('common:cancel')
        };
        toastr.confirm(confirmText, toastrConfirmOptions);
    };
}

export const clearHistoricalResultID = () => ({
    type: types.CLEAR_HISTORICAL_RESULT_ID
});

export const clearHistoricalMPResult = () => ({
    type: types.CLEAR_HISTORICAL_MP_RESULT
});
